import { cloneDeep } from "lodash-es"
import { SimpleEventDispatcher } from "strongly-typed-events"
import type Sign from "./sign"
import { type SignObject } from "./sign_objects"
import { objEmpty, keys } from "@/common/util"
import { matchArray } from "@/signomatic/util"

export interface SignHistoryState {
  hasUndo: boolean
  hasRedo: boolean
}

const allowedKeys = [
  "width",
  "height",
  "widthAuto",
  "heightAuto",
  "thickness",
  "border",
  "color",
  "pattern",
  "contentColor",
  "borderColor",
  "material",
  "shape",
  "fixmethod",
  "preset",
  "contour",
]

const objectAllowedKeys = [
  "left",
  "top",
  "zindex",
  "mirrored",
  "angle",
  "color",
  "text",
  "size",
  "font",
  "bold",
  "italic",
  "underlined",
  "justify",
  "width",
  "height",
  "border",
  "id",
]

const objectThrottledKeys = ["left", "top", "angle", "text", "size", "width", "height"]

interface SignHistoryFrame {
  state: SignState
  objectChanges?: Partial<SignObjectState>
}

class SignHistory {
  readonly onHistoryChangedEvent = new SimpleEventDispatcher<SignHistoryState | null>()
  readonly sign: Sign
  // Past and future arrays are stacks - most recent frame is at the end.
  private past: SignHistoryFrame[] = []
  private future: SignHistoryFrame[] = []
  private removedObjects: SignObject[] = []
  private commitedFrame: SignHistoryFrame | null = null
  private recording = false

  constructor(sign: Sign) {
    this.sign = sign
  }

  get unsavedChanges() {
    return this.past.length > 0 && this.past[this.past.length - 1] !== this.commitedFrame
  }

  /**
   * Called when Signomatic is ready and initial state is complete.
   */
  startRecording() {
    this.recording = true
  }

  /**
   * If changes are detected pushes state before change to the top of undo stack.
   */
  recordPast(state: SignState, update: Partial<SignState>) {
    if (!this.recording || objEmpty(update) || !this.hasAllowedKeys(update) || !this.hasChanges(state, update)) {
      return
    }

    // If we don't clone we will for example get the same reference to sideState as validator and will record the
    // updated value instead.
    // We record complete state because this state has been validated and we won't have to worry about creating
    // invalid state if we try to record and restore only changed keys.
    this.pushUndo(cloneDeep(state))
  }

  recordCopySide(state: SignState, update: Partial<SignState>, removedObjects: SignObject[]) {
    if (!this.recording || objEmpty(update)) {
      return
    }

    for (const obj of removedObjects) {
      if (!this.removedObjects.map(o => o.state.identity).includes(obj.state.identity)) {
        this.removedObjects.push(obj)
      }
    }

    this.pushUndo(state)
  }

  recordObjectPast(state: SignState, object: SignObject, update: Partial<SignObjectState>) {
    if (
      !this.recording ||
      objEmpty(update) ||
      !this.objectHasAllowedKeys(update) ||
      !this.objectHasChanges(object.state, update)
    ) {
      return
    }

    if (this.isThrottledObjectUpdate(update)) {
      return
    }

    this.pushUndo(cloneDeep(state), update)
  }

  recordObjectAdded(state: SignState) {
    this.pushUndo(cloneDeep(state))
  }

  /**
   * Records object removal separately so that we can keep around the removed object in case of undo.
   */
  recordObjectRemoved(state: SignState, object: SignObject) {
    if (!this.removedObjects.map(o => o.state.identity).includes(object.state.identity)) {
      this.removedObjects.push(object)
    }

    this.pushUndo(cloneDeep(state))
  }

  // Apply top undo frame and push current state to the top of redo stack.
  undo() {
    const newState = this.past.pop()

    if (!newState) {
      return
    }

    this.future.push({ state: cloneDeep(this.sign.state) })
    this.applyState(newState.state)
  }

  // Apply top undo frame and push current state to the top of undo stack.
  redo() {
    const newState = this.future.pop()

    if (!newState) {
      return
    }

    this.past.push({ state: cloneDeep(this.sign.state) })
    this.applyState(newState.state)
  }

  // Marks the current frame as submitted to server
  changesCommited() {
    this.commitedFrame = this.past[this.past.length - 1]
  }

  clear() {
    this.past = []
    this.future = []
    this.dispatchChange()
  }

  private dispatchChange() {
    this.onHistoryChangedEvent.dispatch({ hasUndo: this.past.length > 0, hasRedo: this.future.length > 0 })
  }

  /**
   * Pushes state unto undo stack.
   */
  private pushUndo(state: SignState, objectChanges?: Partial<SignObjectState>) {
    this.past.push({ state, objectChanges })

    // Limit history size.
    if (this.past.length > 30) {
      this.past.shift()
    }

    this.future = []

    this.dispatchChange()
  }

  /**
   * Applies either an undo or redo frame taking into account double sided state.
   */
  private applyState(state: SignState) {
    state = { ...state, side: this.sign.state.side }

    if (state.doubleSided && state.sideState) {
      const sideState = state.sideState[this.sign.state.side || 0]

      if (sideState.border) {
        state.border = sideState.border
      }

      if (sideState.color) {
        state.color = sideState.color
      }

      if (sideState.borderColor) {
        state.borderColor = sideState.borderColor
      }
    }

    for (const objState of this.sign.state.objects) {
      const obj = this.sign.getObject(objState)
      const stateObject = state.objects.find(o => o.identity === objState.identity)

      if (obj && !stateObject) {
        this.removedObjects.push(obj)
      }
    }

    for (const obj of state.objects) {
      const removedObject = this.removedObjects.find(o => o.state.identity === obj.identity)

      if (removedObject && !this.sign.state.objects.map(o => o.identity).includes(obj.identity)) {
        this.sign.restoreObjectInstance(removedObject)
      }
    }

    this.sign.setState(state, null, undefined, true)
    this.dispatchChange()
  }

  /**
   * Checks if any of the allowed keys changed.
   */
  private hasChanges(state: SignState, update: Partial<SignState>) {
    for (const key in update) {
      if (allowedKeys.includes(key) && state[key] !== update[key]) {
        return true
      }
    }

    return false
  }

  /**
   * Checks if changes include any of the allowed keys.
   */
  private hasAllowedKeys(update: Partial<SignState>) {
    for (const key in update) {
      if (allowedKeys.includes(key)) {
        return true
      }
    }

    return false
  }

  /**
   * Checks if any of the allowed object keys changed.
   */
  private objectHasChanges(state: SignObjectState, update: Partial<SignObjectState>) {
    for (const key in update) {
      if (objectAllowedKeys.includes(key) && state[key] !== update[key]) {
        return true
      }
    }

    return false
  }

  /**
   * Checks if object changes include any of the allowed keys.
   */
  private objectHasAllowedKeys(update: Partial<SignObjectState>) {
    for (const key in update) {
      if (objectAllowedKeys.includes(key)) {
        return true
      }
    }

    return false
  }

  /**
   * Checks if a continuous object update should only push initial state onto undo stack.
   * For example moving start and finish.
   */
  private isThrottledObjectUpdate(objectUpdate: Partial<SignObjectState>) {
    for (const key in objectUpdate) {
      if (!objectThrottledKeys.includes(key)) {
        return false
      }
    }

    const lastFrame = this.past[this.past.length - 1]

    if (lastFrame?.objectChanges && matchArray(keys(lastFrame.objectChanges), keys(objectUpdate))) {
      return true
    }

    return false
  }
}

export default SignHistory
